import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  MessageSquarePlus, Pencil, X, Check, Trash2, Eraser, Hand, Users, Download, Hash,
  CornerDownRight, Mail, Send, Reply, Image as ImageIcon, Maximize2, Minimize2, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { Spinner, Btn, Textarea } from "./ui-kit";
import { formatDateUK } from "@/lib/format";
import { Dialog, DialogContent } from "./ui/dialog";

// Use a CDN-hosted worker so we don't depend on the static-file server's MIME type
// for `.mjs` (some hosts serve them as application/octet-stream which browsers reject).
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Pin = ({ comment, idx, onClick, active }) => {
  // When a note is clicked in the side list, its pin becomes the "active"
  // pin. Make this visually unmissable so Paul can spot where a change
  // sits even on busy pages with lots of pins:
  //   • swap the regular amber/blue/emerald fill for dark purple
  //   • bump the scale and add a bright purple halo
  //   • a secondary `animate-ping` ring draws the eye for the first beat
  const bgColor = active
    ? "bg-purple-700"
    : comment.resolved
    ? "bg-emerald-500"
    : comment.author_role === "designer"
    ? "bg-blue-600"
    : "bg-amber-500";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(comment); }}
      style={{ left: `${comment.x * 100}%`, top: `${comment.y * 100}%` }}
      className={`absolute -translate-x-1/2 -translate-y-full transition-transform ${active ? "scale-125 z-20" : "hover:scale-110 z-10"}`}
      data-testid={`proof-pin-${comment.id}`}
    >
      {active && (
        <span className="absolute inset-0 -m-1 rounded-full bg-purple-500/60 animate-ping pointer-events-none" aria-hidden="true" />
      )}
      <span
        className={`relative flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold text-white shadow-lg ring-2 ring-white ${bgColor} ${
          active ? "shadow-purple-500/60 shadow-xl ring-[3px] ring-purple-200" : ""
        }`}
      >
        {idx + 1}
      </span>
      <span
        className={`block w-2 h-2 mx-auto -mt-1 rotate-45 ${bgColor}`}
      />
    </button>
  );
};

/**
 * Threaded replies under a single proof note.
 *
 * Renders:
 *   1. Existing replies inline (so they're always visible at a glance)
 *   2. A prominent "Reply" button that opens a dedicated modal containing the
 *      full conversation + a large composer. The modal pattern gives the
 *      reply textarea much more room and makes the "you need to respond"
 *      action far more discoverable than the previous in-list-cell form.
 *
 * The composer behaves differently per role:
 *   • designer (`replyRole="designer"`): includes the recipient picker
 *     (dropdown of the client's contacts) so the reply can be emailed to a
 *     specific person.
 *   • client (`replyRole="client"`): no picker — backend fans out to all
 *     opted-in designers.
 */
const NoteReplies = ({ comment, onAddReply, replyRole, replyContacts }) => {
  const replies = comment.replies || [];
  const [modalOpen, setModalOpen] = useState(false);

  // Highlight the Reply button when there's an unanswered question from the
  // other side: a client looking at a thread where the last reply is from the
  // designer, or vice versa.
  const lastReply = replies[replies.length - 1];
  const otherSideAwaiting = !!(
    lastReply &&
    ((replyRole === "client" && lastReply.author_role === "designer") ||
     (replyRole === "designer" && lastReply.author_role === "client"))
  );

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200" data-testid={`proof-comment-thread-${comment.id}`} onClick={(e) => e.stopPropagation()}>
      {replies.length > 0 && (
        <ul className="space-y-2 mb-3">
          {replies.map((r) => {
            const isDesigner = r.author_role === "designer";
            return (
              <li
                key={r.id}
                className={`pl-3 pr-2 py-2 rounded-md border text-sm leading-relaxed ${isDesigner ? "bg-blue-50 border-blue-200" : "bg-amber-50 border-amber-200"}`}
                data-testid={`proof-comment-reply-${r.id}`}
              >
                <div className="flex items-start gap-2">
                  <CornerDownRight className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isDesigner ? "text-blue-600" : "text-amber-600"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className="text-[11px] font-bold text-zinc-900">
                        {r.author_name || (isDesigner ? "Designer" : "Client")}
                        <span className={`ml-1.5 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDesigner ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-800"}`}>
                          {isDesigner ? "Designer" : "Client"}
                        </span>
                      </span>
                      <span className="text-[10px] text-zinc-500 tabular-nums">
                        {formatDateUK(r.created_at)}
                        {r.emailed_to && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700" title={`Emailed to ${r.emailed_to}`}>
                            · <Mail className="w-3 h-3" /> sent
                          </span>
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-800 whitespace-pre-line mt-0.5 select-text">{r.text}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {onAddReply && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            otherSideAwaiting
              ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm ring-2 ring-blue-200"
              : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
          }`}
          data-testid={`proof-comment-reply-button-${comment.id}`}
        >
          <Reply className="w-4 h-4" />
          {otherSideAwaiting
            ? (replyRole === "client" ? "Reply to designer's question →" : "Reply to client →")
            : (replies.length > 0 ? "Add another reply" : "Reply…")}
        </button>
      )}

      {onAddReply && (
        <ReplyModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          comment={comment}
          onAddReply={onAddReply}
          replyRole={replyRole}
          replyContacts={replyContacts}
        />
      )}
    </div>
  );
};

/**
 * Floating composer for replying to a note.
 *
 * Mirrors the new-note modal's compact-with-expand pattern: by default it
 * pops up as a top-right floating card (proof remains visible behind it);
 * a corner button toggles it into a centred, full-size dialog with a dim
 * backdrop for distraction-free editing.
 */
const ReplyModal = ({ open, onOpenChange, comment, onAddReply, replyRole, replyContacts }) => {
  const replies = comment.replies || [];
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [recipient, setRecipient] = useState(() => {
    if (replyRole !== "designer") return null;
    return (replyContacts && replyContacts[0]) || null;
  });
  const [recipientIdx, setRecipientIdx] = useState(replyContacts && replyContacts.length ? 0 : -1);

  // Note: no reset-on-close effect needed — the component does `if (!open)
  // return null;` below, so closing fully unmounts it and reopening starts
  // with the initial state above.

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || !onAddReply) return;
    setBusy(true);
    try {
      const payload = { text: t };
      if (replyRole === "designer" && recipient) {
        payload.recipient_email = recipient.email;
        payload.recipient_name = recipient.name;
      }
      await onAddReply(comment.id, payload);
      setText("");
      if (replyRole === "designer") {
        if (recipient?.email) {
          toast.success("Reply sent", { description: `Emailed to ${recipient.email}.` });
        } else {
          toast.success("Reply saved", { description: "No email recipient picked — the reply is visible on the proof only." });
        }
      } else {
        toast.success("Reply sent to the designer.");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error("Reply failed", { description: err?.response?.data?.detail || err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const isDesignerOriginal = comment.author_role === "designer";
  const accentBorder = isDesignerOriginal ? "border-blue-300" : "border-amber-300";
  const accentBg = isDesignerOriginal ? "bg-blue-50" : "bg-amber-50";
  const accentText = isDesignerOriginal ? "text-blue-800" : "text-amber-800";
  const accentLabel = isDesignerOriginal ? "text-blue-700" : "text-amber-700";
  const accentHeading = isDesignerOriginal ? "text-blue-950" : "text-amber-950";
  const accentHover = isDesignerOriginal ? "hover:bg-blue-100" : "hover:bg-amber-100";

  return (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid={`proof-reply-modal-${comment.id}`}>
      {expanded && (
        <button
          type="button"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          className="absolute inset-0 bg-zinc-950/40 backdrop-blur-sm pointer-events-auto"
          data-testid={`proof-reply-modal-backdrop-${comment.id}`}
        />
      )}
      <div
        className={`absolute pointer-events-auto bg-white border-2 ${accentBorder} rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all ${
          expanded
            ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-3xl max-h-[88vh]"
            : "right-4 sm:right-6 top-24 w-[440px] max-w-[calc(100vw-2rem)] max-h-[80vh]"
        }`}
        data-expanded={expanded ? "true" : "false"}
      >
        <div className={`px-5 py-3.5 border-b ${accentBorder} ${accentBg} flex items-start justify-between gap-3`}>
          <div className="min-w-0">
            <p className={`text-[10px] font-bold uppercase tracking-wider ${accentLabel}`}>
              Reply · page {comment.page}
            </p>
            <h3 className={`text-base font-display font-bold ${accentHeading} leading-tight mt-0.5`}>
              {replyRole === "designer" ? "Reply to client" : "Reply to designer"}
            </h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className={`p-1.5 rounded-full ${accentText} ${accentHover}`}
              title={expanded ? "Shrink modal" : "Expand modal"}
              data-testid={`proof-reply-modal-expand-${comment.id}`}
            >
              {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className={`p-1.5 rounded-full ${accentText} ${accentHover} disabled:opacity-40`}
              title="Cancel"
              data-testid={`proof-reply-modal-cancel-${comment.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 proof-scroll">
          {/* Original note */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid={`proof-reply-modal-original-${comment.id}`}>
            <div className="flex items-baseline justify-between gap-2 mb-1.5 flex-wrap">
              <span className="text-xs font-bold text-zinc-900">
                {comment.author_name || (isDesignerOriginal ? "Designer" : "Client")}
                <span className={`ml-1.5 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDesignerOriginal ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-800"}`}>
                  {isDesignerOriginal ? "Designer" : "Client"}
                </span>
              </span>
              <span className="text-[10px] text-zinc-500 tabular-nums">{formatDateUK(comment.created_at)}</span>
            </div>
            <p className="text-sm text-zinc-900 whitespace-pre-line leading-relaxed select-text">{comment.text}</p>
          </div>

          {/* Existing thread */}
          {replies.length > 0 && (
            <div className="space-y-2" data-testid={`proof-reply-modal-thread-${comment.id}`}>
              {replies.map((r) => {
                const isDesigner = r.author_role === "designer";
                return (
                  <div
                    key={r.id}
                    className={`pl-3 pr-3 py-2 rounded-md border text-sm leading-relaxed ${isDesigner ? "bg-blue-50 border-blue-200" : "bg-amber-50 border-amber-200"}`}
                  >
                    <div className="flex items-start gap-2">
                      <CornerDownRight className={`w-4 h-4 mt-0.5 shrink-0 ${isDesigner ? "text-blue-600" : "text-amber-600"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap mb-0.5">
                          <span className="text-xs font-bold text-zinc-900">
                            {r.author_name || (isDesigner ? "Designer" : "Client")}
                            <span className={`ml-1.5 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDesigner ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-800"}`}>
                              {isDesigner ? "Designer" : "Client"}
                            </span>
                          </span>
                          <span className="text-[10px] text-zinc-500 tabular-nums">
                            {formatDateUK(r.created_at)}
                            {r.emailed_to && (
                              <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700" title={`Emailed to ${r.emailed_to}`}>
                                · <Mail className="w-3 h-3" /> sent
                              </span>
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-800 whitespace-pre-line select-text">{r.text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Composer */}
          <form onSubmit={send} className="space-y-3 pt-1" data-testid={`proof-reply-modal-form-${comment.id}`}>
            {replyRole === "designer" && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" />Email to
                </label>
                <select
                  value={recipientIdx}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "-1") { setRecipientIdx(-1); setRecipient(null); return; }
                    const idx = parseInt(v, 10);
                    setRecipientIdx(idx);
                    setRecipient(replyContacts[idx]);
                  }}
                  className="w-full text-sm bg-white border border-zinc-300 rounded-md px-3 py-2"
                  data-testid={`proof-reply-modal-recipient-${comment.id}`}
                >
                  {(replyContacts || []).length === 0 && (
                    <option value="-1">(no contacts on file — saves to proof only)</option>
                  )}
                  {(replyContacts || []).map((c, idx) => (
                    <option key={c.email} value={idx}>{c.name} &lt;{c.email}&gt;</option>
                  ))}
                  {(replyContacts || []).length > 0 && (
                    <option value="-1">— save to proof only (no email) —</option>
                  )}
                </select>
              </div>
            )}
            <Textarea
              rows={expanded ? 9 : 5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                replyRole === "designer"
                  ? "Write a reply — the client will get this in their inbox with a link back to the proof."
                  : "Reply to the designer — they'll be notified by email."
              }
              disabled={busy}
              autoFocus
              data-testid={`proof-reply-modal-text-${comment.id}`}
              className="!text-sm"
            />
          </form>
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="px-3.5 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-200 rounded-full disabled:opacity-40"
          >Cancel</button>
          <Btn type="button" size="sm" onClick={send} disabled={busy || !text.trim()} data-testid={`proof-reply-modal-send-${comment.id}`}>
            <Send className="w-3.5 h-3.5" />
            {busy
              ? "Sending…"
              : replyRole === "designer"
              ? (recipient?.email ? `Send to ${recipient.name?.split(/\s+/)[0] || "client"}` : "Save reply")
              : "Send reply"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

/**
 * Image attachments under a single proof comment. Both client and designer
 * see the existing thumbnails; only the original note's author can add/remove
 * (matched server-side via the author_name query param).
 *
 * • Max 3 images per note, ≤ 8 MB each (caps enforced on the backend too).
 * • Click a thumbnail to expand it in a lightbox.
 */
const PROOF_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
const PROOF_ATTACHMENT_MAX_PER_COMMENT = 3;

const NoteAttachments = ({ comment, attachmentBaseUrl, onUpload, onDelete, canEdit }) => {
  const attachments = comment.attachments || [];
  const [busy, setBusy] = useState(false);
  const [lightboxId, setLightboxId] = useState(null);
  const fileInputRef = useRef(null);

  const remaining = PROOF_ATTACHMENT_MAX_PER_COMMENT - attachments.length;

  const handlePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !onUpload) return;
    const slots = Math.max(0, remaining);
    const toUpload = files.slice(0, slots);
    const skipped = files.length - toUpload.length;
    if (skipped > 0) {
      toast.warning(`Only ${slots} more image${slots === 1 ? "" : "s"} allowed — skipped ${skipped}.`);
    }
    const oversized = toUpload.filter((f) => f.size > PROOF_ATTACHMENT_MAX_BYTES);
    const goodFiles = toUpload.filter((f) => f.size <= PROOF_ATTACHMENT_MAX_BYTES);
    if (oversized.length) {
      toast.error(`${oversized.length} image${oversized.length === 1 ? " is" : "s are"} larger than 8 MB and weren't uploaded.`);
    }
    if (!goodFiles.length) return;
    setBusy(true);
    try {
      for (const file of goodFiles) {
        await onUpload(comment.id, file);
      }
      toast.success(`${goodFiles.length} image${goodFiles.length === 1 ? "" : "s"} uploaded.`);
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (attId) => {
    if (!onDelete) return;
    try {
      await onDelete(comment.id, attId);
      toast.success("Image removed.");
      setLightboxId(null);
    } catch (err) {
      toast.error("Couldn't remove image", { description: err?.response?.data?.detail || err?.message });
    }
  };

  if (!attachments.length && !canEdit) return null;

  const activeAtt = attachments.find((a) => a.id === lightboxId);

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200" onClick={(e) => e.stopPropagation()} data-testid={`proof-comment-attachments-${comment.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Images {attachments.length > 0 && <span className="text-zinc-700 normal-case tracking-normal">({attachments.length}/{PROOF_ATTACHMENT_MAX_PER_COMMENT})</span>}
        </span>
        {canEdit && onUpload && remaining > 0 && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
              multiple
              hidden
              onChange={handlePick}
              data-testid={`proof-comment-attachment-input-${comment.id}`}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="text-[11px] font-medium text-blue-700 hover:text-blue-900 underline"
              data-testid={`proof-comment-attachment-add-${comment.id}`}
            >
              {busy ? "Uploading…" : `+ Add image${remaining > 1 ? `s (${remaining} left)` : ""}`}
            </button>
          </>
        )}
      </div>
      {attachments.length === 0 ? (
        <p className="text-xs text-zinc-400 italic">
          {canEdit ? "Optionally add up to 3 images (e.g. a reference photo). Max 8 MB each." : "No images on this note."}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {attachments.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setLightboxId(a.id)}
              className="relative group block aspect-square rounded-md overflow-hidden border-2 border-zinc-200 hover:border-zinc-400 bg-zinc-100"
              data-testid={`proof-comment-attachment-thumb-${a.id}`}
              title={a.original_name}
            >
              <img
                src={`${attachmentBaseUrl}/${a.id}`}
                alt={a.original_name}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxId} onOpenChange={(o) => !o && setLightboxId(null)}>
        <DialogContent className="max-w-4xl bg-zinc-950 border-zinc-800 p-0 overflow-hidden" data-testid={`proof-comment-attachment-lightbox-${comment.id}`}>
          {activeAtt && (
            <div className="relative">
              <img
                src={`${attachmentBaseUrl}/${activeAtt.id}`}
                alt={activeAtt.original_name}
                className="block w-full max-h-[85vh] object-contain bg-black"
              />
              <div className="absolute top-0 inset-x-0 flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-b from-black/70 to-transparent text-white text-xs">
                <span className="truncate">{activeAtt.original_name} · {Math.round((activeAtt.size || 0) / 1024)} KB</span>
                {canEdit && onDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(activeAtt.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600/90 hover:bg-red-600 text-white text-[11px] font-semibold"
                    data-testid={`proof-comment-attachment-delete-${activeAtt.id}`}
                  >
                    <Trash2 className="w-3 h-3" />Remove
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Floating modal used when the user drops a new pin on the proof.
 *
 * Defaults to a compact card pinned to the top-right of the viewport so the
 * proof itself stays visible while the user types. A toggle in the corner
 * expands it to a centred dialog with a dim backdrop for distraction-free
 * editing. Clients can attach up to 3 images (≤ 8 MB each); files are queued
 * locally and uploaded against the new comment once it's saved server-side.
 */
const NewNoteModal = ({ open, page, replyRole, onUploadAttachment, onSubmit, onCancel }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]); // [{key, file, previewUrl}]
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const canAttach = replyRole === "client" && typeof onUploadAttachment === "function";
  const remaining = PROOF_ATTACHMENT_MAX_PER_COMMENT - files.length;

  // Note: no reset-on-close effect needed — `if (!open) return null;` at the
  // end of this component means closing fully unmounts it. Reopening starts
  // with fresh initial state from the useState calls above. Object-URL
  // previews are revoked on individual file removal; any preview URLs still
  // alive at unmount (max 3) are released when the page navigates away.

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    const slots = Math.max(0, remaining);
    const toAdd = picked.slice(0, slots);
    const skipped = picked.length - toAdd.length;
    if (skipped > 0) toast.warning(`Only ${slots} more image${slots === 1 ? "" : "s"} allowed — skipped ${skipped}.`);
    const good = toAdd.filter((f) => f.size <= PROOF_ATTACHMENT_MAX_BYTES);
    const oversized = toAdd.length - good.length;
    if (oversized > 0) toast.error(`${oversized} image${oversized === 1 ? " is" : "s are"} larger than 8 MB and weren't added.`);
    if (!good.length) return;
    setFiles((prev) => [
      ...prev,
      ...good.map((f) => ({ key: `${Date.now()}-${f.name}-${f.size}`, file: f, previewUrl: URL.createObjectURL(f) })),
    ]);
  };

  const removeFile = (key) => {
    setFiles((prev) => {
      const target = prev.find((x) => x.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((x) => x.key !== key);
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await onSubmit(t, files.map((f) => f.file));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="proof-new-note-modal">
      {expanded && (
        <button
          type="button"
          aria-label="Close"
          onClick={onCancel}
          className="absolute inset-0 bg-zinc-950/40 backdrop-blur-sm pointer-events-auto"
          data-testid="proof-new-note-backdrop"
        />
      )}
      <div
        className={`absolute pointer-events-auto bg-white border-2 border-amber-300 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all ${
          expanded
            ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-3xl max-h-[88vh]"
            : "right-4 sm:right-6 top-24 w-[420px] max-w-[calc(100vw-2rem)] max-h-[80vh]"
        }`}
        data-expanded={expanded ? "true" : "false"}
      >
        <div className="px-5 py-3.5 border-b border-amber-200 bg-amber-50 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">New note · page {page}</p>
            <h3 className="text-base font-display font-bold text-amber-950 leading-tight mt-0.5">
              {canAttach ? "Add a note (with optional images)" : "Add a note"}
            </h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="p-1.5 rounded-full text-amber-800 hover:bg-amber-100"
              title={expanded ? "Shrink modal" : "Expand modal"}
              data-testid="proof-new-note-expand"
            >
              {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="p-1.5 rounded-full text-amber-800 hover:bg-amber-100 disabled:opacity-40"
              title="Cancel"
              data-testid="proof-new-note-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 proof-scroll">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 block">Your note</label>
            <Textarea
              autoFocus
              rows={expanded ? 10 : 5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your note here…"
              disabled={busy}
              data-testid="proof-new-note-text"
              className="!text-sm"
            />
          </div>

          {canAttach && (
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  Images
                  <span className="text-zinc-700 normal-case tracking-normal font-medium">({files.length}/{PROOF_ATTACHMENT_MAX_PER_COMMENT})</span>
                </label>
                {remaining > 0 && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                      multiple
                      hidden
                      onChange={handlePick}
                      data-testid="proof-new-note-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={busy}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 hover:text-blue-900 underline disabled:opacity-40"
                      data-testid="proof-new-note-add-image"
                    >
                      <Paperclip className="w-3 h-3" />
                      Add image{remaining > 1 ? `s (${remaining} left)` : ""}
                    </button>
                  </>
                )}
              </div>
              {files.length === 0 ? (
                <p className="text-xs text-zinc-400 italic">Optionally add up to {PROOF_ATTACHMENT_MAX_PER_COMMENT} images (e.g. a reference photo). Max 8 MB each.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {files.map((f) => (
                    <div key={f.key} className="relative group aspect-square rounded-md overflow-hidden border-2 border-zinc-200 bg-zinc-100" data-testid={`proof-new-note-thumb-${f.key}`}>
                      <img src={f.previewUrl} alt={f.file.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeFile(f.key)}
                        disabled={busy}
                        className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-zinc-900/80 hover:bg-red-600 text-white shadow-sm disabled:opacity-40"
                        title="Remove"
                        data-testid={`proof-new-note-thumb-remove-${f.key}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-[10px] text-white truncate">
                        {Math.round(f.file.size / 1024)} KB
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>

        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3.5 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-200 rounded-full disabled:opacity-40"
            data-testid="proof-new-note-cancel"
          >Cancel</button>
          <Btn
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={!text.trim() || busy}
            data-testid="proof-new-note-submit"
          >
            {busy
              ? (files.length > 0 ? "Posting & uploading…" : "Posting…")
              : (files.length > 0 ? `Post note + ${files.length} image${files.length === 1 ? "" : "s"}` : "Post note")}
          </Btn>
        </div>
      </div>
    </div>
  );
};


const PdfProofViewer = ({
  fileUrl,
  comments = [],
  annotations = [],
  pageNotes = {},
  onAddComment,
  onUpdatePageNotes,
  onDeleteComment,
  onResolveComment,
  onAddAnnotation,
  onDeleteAnnotation,
  onResolveAnnotation,
  onAddReply,           // (commentId, {text, recipient_email, recipient_name}) => Promise<reply>
  replyRole = "designer", // "designer" | "client" — controls whether the email dropdown is shown
  replyContacts = [],   // designer-side: [{name, email}] from the client's contacts list
  onUploadAttachment,   // (commentId, File) => Promise<attachment>   — only on public/client side
  onDeleteAttachment,   // (commentId, attachmentId) => Promise<void> — only on public/client side
  attachmentBaseUrl,    // string — used to render <img src=...> for existing attachments
  authorName,           // display name for new replies (e.g. "Designer" or the client's name)
  canEdit = true,
  canDelete = () => false,
  canDeleteAnnotation = () => false,
  downloadUrl,
  downloadFilename,
  downloadSizeLabel,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(700);
  const [tool, setTool] = useState("none"); // none | note | draw
  const [pendingPin, setPendingPin] = useState(null);
  const [pendingText, setPendingText] = useState("");
  const [activeComment, setActiveComment] = useState(null);
  const [pageNoteDraft, setPageNoteDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [pendingDrawing, setPendingDrawing] = useState(null); // {points, text}
  const [panning, setPanning] = useState(false);
  const panStartRef = useRef(null); // {x, y, scrollLeft, scrollTop}
  const containerRef = useRef(null);
  const pageWrapperRef = useRef(null);
  const noteSaveTimer = useRef(null);
  const thumbRailRef = useRef(null); // for auto-scrolling the active thumbnail into view
  const [jumpInput, setJumpInput] = useState(""); // bound to the "Jump to page…" search box

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth - 32);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Re-measure once the PDF loads (numPages becomes known) — the thumbnail rail
  // only appears when numPages > 1, which changes the available width.
  useEffect(() => {
    if (!containerRef.current) return;
    // next tick — let layout settle after rail mounts
    const id = requestAnimationFrame(() => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth - 32);
    });
    return () => cancelAnimationFrame(id);
  }, [numPages]);

  // Keep the active thumbnail visible — when the user jumps to a page via the
  // "Jump to page" box or the arrow keys, scroll the corresponding thumbnail
  // into view so the rail mirrors the main viewer.
  useEffect(() => {
    if (!thumbRailRef.current) return;
    const el = thumbRailRef.current.querySelector(`[data-testid="proof-thumb-${page}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [page, numPages]);

  // When a note is selected (via clicking its pin on the canvas), make sure the
  // matching row in the notes list scrolls into view so the user can read /
  // copy it without hunting through the list.
  useEffect(() => {
    if (!activeComment) return;
    const el = document.querySelector(`[data-testid="proof-comment-item-${activeComment.id}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeComment]);

  const handleJumpSubmit = (e) => {
    e?.preventDefault?.();
    const n = parseInt(jumpInput, 10);
    if (Number.isFinite(n) && n >= 1 && numPages && n <= numPages) {
      goToPage(n);
    }
    setJumpInput("");
  };

  // Warn before leaving if there's an unsaved pin/drawing in flight.
  useEffect(() => {
    const hasPending =
      !!pendingPin ||
      (pendingDrawing && (pendingDrawing.points?.length > 1));
    if (!hasPending) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "You have an unsaved note. Leave the page anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pendingPin, pendingDrawing]);

  // Centralised page-change so every navigator (prev/next/jump/thumb) resets
  // the transient editor state (active comment, pending pin/drawing, draft
  // textarea sync) without needing a useEffect. Keeps the page-change atomic
  // and avoids the React 19 set-state-in-effect anti-pattern.
  const goToPage = (next) => {
    setPage((cur) => {
      const resolved = typeof next === "function" ? next(cur) : next;
      if (resolved !== cur) {
        setPageNoteDraft(pageNotes[String(resolved)] || "");
        setActiveComment(null);
        setPendingPin(null);
        setPendingText("");
        setCurrentStroke([]);
        setPendingDrawing(null);
      }
      return resolved;
    });
  };

  const pageComments = comments.filter((c) => c.page === page);
  const pageAnnotations = annotations.filter((a) => a.page === page);

  const getRel = (e) => {
    const rect = pageWrapperRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: Math.max(0, Math.min(1, (point.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (point.clientY - rect.top) / rect.height)),
    };
  };

  const handlePageClick = (e) => {
    if (tool !== "note" || !canEdit) return;
    const { x, y } = getRel(e);
    setPendingPin({ x, y });
    setPendingText("");
  };

  const handlePointerDown = (e) => {
    if (tool !== "draw" || !canEdit) return;
    e.preventDefault();
    const pt = getRel(e);
    setDrawing(true);
    setCurrentStroke([pt]);
  };

  // Pan/drag-to-scroll when the Hand tool is active.
  const handleContainerMouseDown = (e) => {
    if (tool !== "pan" || !containerRef.current) return;
    e.preventDefault();
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop,
    };
    setPanning(true);
  };

  const handleContainerMouseMove = (e) => {
    if (!panning || !panStartRef.current || !containerRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    containerRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
    containerRef.current.scrollTop = panStartRef.current.scrollTop - dy;
  };

  const endPan = () => {
    if (!panning) return;
    setPanning(false);
    panStartRef.current = null;
  };

  const handlePointerMove = (e) => {
    if (!drawing || tool !== "draw") return;
    e.preventDefault();
    const pt = getRel(e);
    setCurrentStroke((s) => {
      const last = s[s.length - 1];
      if (last && Math.abs(last.x - pt.x) < 0.002 && Math.abs(last.y - pt.y) < 0.002) return s;
      return [...s, pt];
    });
  };

  const handlePointerUp = async () => {
    if (!drawing || tool !== "draw") return;
    setDrawing(false);
    if (currentStroke.length > 1) {
      // Hold onto the stroke and ask for a note before saving
      setPendingDrawing({ points: currentStroke, text: "" });
    }
    setCurrentStroke([]);
  };

  const submitPendingDrawing = async () => {
    if (!pendingDrawing) return;
    try {
      await onAddAnnotation?.(page, pendingDrawing.points, "#ef4444", pendingDrawing.text.trim());
      toast.success("Drawing saved", { description: pendingDrawing.text.trim() || `Page ${page}` });
    } catch {
      toast.error("Couldn't save drawing");
      return;
    }
    setPendingDrawing(null);
    setTool("none");
  };

  const cancelPendingDrawing = () => {
    setPendingDrawing(null);
    setTool("none");
  };

  const submitPendingPin = async (text, files = []) => {
    if (!pendingPin || !text?.trim()) return;
    let newComment = null;
    try {
      newComment = await onAddComment?.(page, pendingPin.x, pendingPin.y, text.trim());
    } catch {
      toast.error("Couldn't save note");
      return;
    }
    // If the client queued image attachments in the modal, upload them now
    // against the just-created comment. Failures are reported but the note
    // itself is already saved so we don't tear it down.
    if (newComment?.id && files.length > 0 && typeof onUploadAttachment === "function") {
      let uploaded = 0;
      let failed = 0;
      for (const file of files) {
        try {
          await onUploadAttachment(newComment.id, file);
          uploaded += 1;
        } catch (err) {
          failed += 1;
          console.error("Attachment upload failed:", err);
        }
      }
      if (failed === 0) {
        toast.success("Note saved", { description: `${uploaded} image${uploaded === 1 ? "" : "s"} attached.` });
      } else if (uploaded === 0) {
        toast.error("Note saved, but no images could be uploaded.");
      } else {
        toast.warning(`Note saved with ${uploaded} image${uploaded === 1 ? "" : "s"}; ${failed} failed.`);
      }
    } else {
      toast.success("Note saved", { description: text.trim() });
    }
    setPendingPin(null);
    setPendingText("");
    setTool("none");
  };

  const cancelPendingPin = () => {
    setPendingPin(null);
    setPendingText("");
    setTool("none");
  };

  const handleNotesChange = (val) => {
    setPageNoteDraft(val);
    if (!canEdit) return;
    if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current);
    setSavingNotes(true);
    noteSaveTimer.current = setTimeout(async () => {
      await onUpdatePageNotes?.(page, val);
      setSavingNotes(false);
    }, 700);
  };

  const buildPath = (points) => {
    if (!points || points.length === 0) return "";
    return points.map((p, i) => `${i === 0 ? "M" : "L"} ${(p.x * 100).toFixed(3)} ${(p.y * 100).toFixed(3)}`).join(" ");
  };

  const cursorClass =
    tool === "pan" ? (panning ? "cursor-grabbing" : "cursor-grab")
    : tool === "note" && canEdit ? "cursor-crosshair"
    : tool === "draw" && canEdit ? "cursor-crosshair"
    : "";

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4 h-full">
      <div className="flex flex-col bg-zinc-100 rounded-xl overflow-hidden border border-zinc-200">
        {/* BIG TOOLBAR */}
        {canEdit && (
          <div className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-2 flex-wrap" data-testid="proof-toolbar">
            <button
              type="button"
              onClick={() => { setTool(tool === "note" ? "none" : "note"); setPendingPin(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm ${
                tool === "note"
                  ? "bg-amber-500 text-white ring-4 ring-amber-200"
                  : "bg-amber-50 text-amber-800 border-2 border-amber-300 hover:bg-amber-100"
              }`}
              data-testid="proof-tool-note"
            >
              <MessageSquarePlus className="w-5 h-5" />
              {tool === "note" ? "Click anywhere on the page" : "Add note"}
            </button>
            <button
              type="button"
              onClick={() => { setTool(tool === "draw" ? "none" : "draw"); setPendingPin(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm ${
                tool === "draw"
                  ? "bg-red-500 text-white ring-4 ring-red-200"
                  : "bg-red-50 text-red-700 border-2 border-red-300 hover:bg-red-100"
              }`}
              data-testid="proof-tool-draw"
            >
              <Pencil className="w-5 h-5" />
              {tool === "draw" ? "Drag to draw on the page" : "Draw on page"}
            </button>
            <button
              type="button"
              onClick={() => { setTool(tool === "pan" ? "none" : "pan"); setPendingPin(null); setCurrentStroke([]); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-sm ${
                tool === "pan"
                  ? "bg-zinc-900 text-white ring-4 ring-zinc-300"
                  : "bg-zinc-50 text-zinc-700 border-2 border-zinc-300 hover:bg-zinc-100"
              }`}
              title="Drag to pan when zoomed in"
              data-testid="proof-tool-pan"
            >
              <Hand className="w-5 h-5" />
              {tool === "pan" ? "Drag to pan" : "Pan"}
            </button>
            {tool !== "none" && (
              <button
                type="button"
                onClick={() => { setTool("none"); setPendingPin(null); setCurrentStroke([]); }}
                className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 ml-2"
                data-testid="proof-tool-cancel"
              ><X className="w-3 h-3" />Cancel tool</button>
            )}
            {downloadUrl && (
              <a
                href={downloadUrl}
                download={downloadFilename || "proof.pdf"}
                className="ml-auto flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-zinc-900 text-white hover:bg-zinc-800 shadow-sm transition-colors"
                data-testid="proof-toolbar-download"
                title={`Download a clean, un-marked-up copy${downloadFilename ? ` of ${downloadFilename}` : ""}`}
              >
                <Download className="w-4 h-4" />
                <span>Download original PDF</span>
                {downloadSizeLabel && (
                  <span className="text-zinc-300 font-normal hidden md:inline">· {downloadSizeLabel}</span>
                )}
              </a>
            )}
            <div className={`${downloadUrl ? "" : "ml-auto"} flex items-center gap-1`}>
              <button onClick={() => goToPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40" data-testid="proof-prev-page"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-xs text-zinc-700 font-medium tabular-nums min-w-[60px] text-center">{numPages ? `${page} / ${numPages}` : "…"}</span>
              <button onClick={() => goToPage((p) => Math.min(numPages || p, p + 1))} disabled={!numPages || page >= numPages} className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40" data-testid="proof-next-page"><ChevronRight className="w-4 h-4" /></button>
              <span className="w-px h-5 bg-zinc-200 mx-1" />
              <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)))} className="p-1.5 rounded hover:bg-zinc-100"><ZoomOut className="w-4 h-4" /></button>
              <span className="text-xs text-zinc-500 tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)))} className="p-1.5 rounded hover:bg-zinc-100"><ZoomIn className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {!canEdit && (
          <div className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-2 justify-end flex-wrap">
            {downloadUrl && (
              <a
                href={downloadUrl}
                download={downloadFilename || "proof.pdf"}
                className="mr-auto flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-zinc-900 text-white hover:bg-zinc-800 shadow-sm transition-colors"
                data-testid="proof-toolbar-download"
                title={`Download a clean, un-marked-up copy${downloadFilename ? ` of ${downloadFilename}` : ""}`}
              >
                <Download className="w-4 h-4" />
                <span>Download original PDF</span>
                {downloadSizeLabel && (
                  <span className="text-zinc-300 font-normal hidden md:inline">· {downloadSizeLabel}</span>
                )}
              </a>
            )}
            <button
              type="button"
              onClick={() => setTool(tool === "pan" ? "none" : "pan")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all shadow-sm ${
                tool === "pan"
                  ? "bg-zinc-900 text-white"
                  : "bg-zinc-50 text-zinc-700 border border-zinc-300 hover:bg-zinc-100"
              }`}
              title="Drag to pan when zoomed in"
              data-testid="proof-tool-pan"
            >
              <Hand className="w-3.5 h-3.5" />
              {tool === "pan" ? "Panning" : "Pan"}
            </button>
            <span className="w-px h-5 bg-zinc-200 mx-1" />
            <button onClick={() => goToPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs text-zinc-700 font-medium tabular-nums min-w-[60px] text-center">{numPages ? `${page} / ${numPages}` : "…"}</span>
            <button onClick={() => goToPage((p) => Math.min(numPages || p, p + 1))} disabled={!numPages || page >= numPages} className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
            <span className="w-px h-5 bg-zinc-200 mx-1" />
            <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)))} className="p-1.5 rounded hover:bg-zinc-100"><ZoomOut className="w-4 h-4" /></button>
            <span className="text-xs text-zinc-500 tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)))} className="p-1.5 rounded hover:bg-zinc-100"><ZoomIn className="w-4 h-4" /></button>
          </div>
        )}

        <Document
          file={fileUrl}
          className="flex flex-1 min-h-0"
          onLoadSuccess={(d) => { setNumPages(d.numPages); setPage(1); }}
          loading={<div className="flex-1 py-20 flex justify-center"><Spinner /></div>}
          error={<p className="flex-1 text-sm text-red-600 py-10 text-center">Could not load PDF.</p>}
        >
          <div
            ref={containerRef}
            className="flex-1 overflow-auto py-4 px-4 text-center proof-scroll min-w-0"
            onMouseDown={handleContainerMouseDown}
            onMouseMove={handleContainerMouseMove}
            onMouseUp={endPan}
            onMouseLeave={endPan}
          >
              <div
                ref={pageWrapperRef}
                onClick={handlePageClick}
                onMouseDown={handlePointerDown}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerUp}
                onMouseLeave={handlePointerUp}
                onTouchStart={handlePointerDown}
                onTouchMove={handlePointerMove}
                onTouchEnd={handlePointerUp}
                className={`relative inline-block shadow-md rounded-md overflow-hidden bg-white ${cursorClass}`}
                style={{ touchAction: tool === "draw" ? "none" : "auto" }}
              >
                <Page
                  pageNumber={page}
                  width={containerWidth * scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
                {/* Drawing overlay */}
                <svg
                  className="absolute inset-0 pointer-events-none"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ width: "100%", height: "100%" }}
                >
                  {pageAnnotations.map((a) => {
                    const strokeColor = a.resolved ? "#10b981" : (a.color || "#ef4444");
                    return (
                    <g key={a.id} className="proof-annotation-group">
                      <path
                        d={buildPath(a.points)}
                        stroke={strokeColor}
                        strokeWidth={1.4}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                        vectorEffect="non-scaling-stroke"
                        style={{
                          filter: "drop-shadow(0 0 1px rgba(0,0,0,0.35))",
                          opacity: a.resolved ? 0.55 : 1,
                        }}
                      />
                      {canDeleteAnnotation(a) && onDeleteAnnotation && (
                        <foreignObject
                          x={Math.max(...a.points.map((p) => p.x)) * 100 - 4}
                          y={Math.min(...a.points.map((p) => p.y)) * 100 - 6}
                          width="6"
                          height="6"
                          style={{ pointerEvents: "auto" }}
                        >
                          <button
                            onClick={(e) => { e.stopPropagation(); onDeleteAnnotation(a.id); }}
                            title="Delete drawing"
                            className="bg-white border border-zinc-300 rounded-full text-red-500 hover:bg-red-50 grid place-items-center"
                            style={{ width: "100%", height: "100%" }}
                            data-testid={`proof-annotation-delete-${a.id}`}
                          >
                            <Eraser style={{ width: "60%", height: "60%" }} />
                          </button>
                        </foreignObject>
                      )}
                    </g>
                    );
                  })}
                  {currentStroke.length > 1 && (
                    <path
                      d={buildPath(currentStroke)}
                      stroke="#ef4444"
                      strokeWidth={1.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  {pendingDrawing && (
                    <path
                      d={buildPath(pendingDrawing.points)}
                      stroke="#ef4444"
                      strokeWidth={1.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      vectorEffect="non-scaling-stroke"
                      style={{ filter: "drop-shadow(0 0 1px rgba(0,0,0,0.35))" }}
                    />
                  )}
                </svg>
                {/* Pins */}
                {pageComments.map((c) => {
                  const idx = comments.findIndex((x) => x.id === c.id);
                  return (
                    <Pin key={c.id} comment={c} idx={idx} active={activeComment?.id === c.id} onClick={(comment) => setActiveComment(comment)} />
                  );
                })}
                {pendingPin && (
                  <div style={{ left: `${pendingPin.x * 100}%`, top: `${pendingPin.y * 100}%` }} className="absolute -translate-x-1/2 -translate-y-full pointer-events-none">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-500 text-white text-[11px] font-bold ring-2 ring-white shadow-lg animate-pulse">+</span>
                  </div>
                )}
              </div>
            </div>
            {numPages > 1 && (
              <aside
                className="w-[112px] shrink-0 border-l border-zinc-200 bg-white/70 flex flex-col"
                data-testid="proof-thumbnail-rail"
              >
                {/* Jump to page — small sticky form at the top of the rail */}
                <form
                  onSubmit={handleJumpSubmit}
                  className="px-2 pt-2 pb-1.5 border-b border-zinc-200 bg-white/95 sticky top-0 z-10"
                  data-testid="proof-thumb-jump-form"
                >
                  <label className="block text-[9px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Jump to</label>
                  <div className="relative">
                    <Hash className="w-3 h-3 text-zinc-400 absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={numPages}
                      value={jumpInput}
                      onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ""))}
                      onBlur={handleJumpSubmit}
                      placeholder={`1–${numPages}`}
                      className="w-full pl-5 pr-1 py-1 text-xs rounded border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      data-testid="proof-thumb-jump-input"
                    />
                  </div>
                </form>
                <div ref={thumbRailRef} className="flex-1 overflow-y-auto py-3 px-2 flex flex-col gap-2 proof-scroll min-h-0">
                {Array.from({ length: numPages }, (_, i) => i + 1).map((pn) => {
                  const pageComs = comments.filter((c) => c.page === pn);
                  const pageAnns = annotations.filter((a) => a.page === pn);
                  const total = pageComs.length + pageAnns.length;
                  const unresolved =
                    pageComs.filter((c) => !c.resolved).length +
                    pageAnns.filter((a) => !a.resolved).length;
                  const allResolved = total > 0 && unresolved === 0;
                  const badgeColor = allResolved ? "bg-emerald-500" : "bg-amber-500";
                  const isActive = pn === page;
                  return (
                    <button
                      key={pn}
                      type="button"
                      onClick={() => goToPage(pn)}
                      className={`relative group rounded-md overflow-hidden border-2 transition-all bg-white shrink-0 ${
                        isActive
                          ? "border-amber-500 ring-2 ring-amber-200 shadow-md"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`proof-thumb-${pn}`}
                      title={`Go to page ${pn}${total > 0 ? ` · ${total} note${total !== 1 ? "s" : ""}${allResolved ? " (all resolved)" : unresolved > 0 ? ` · ${unresolved} unresolved` : ""}` : ""}`}
                    >
                      <div className="pointer-events-none flex justify-center">
                        <Page
                          pageNumber={pn}
                          width={92}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={<div className="h-28 grid place-items-center"><Spinner /></div>}
                        />
                      </div>
                      <div className={`text-[10px] font-semibold text-center py-0.5 ${isActive ? "bg-amber-500 text-white" : "bg-zinc-50 text-zinc-600 group-hover:bg-zinc-100"}`}>
                        {pn}
                      </div>
                      {total > 0 && (
                        <span
                          className={`absolute top-1 right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full text-white shadow ring-2 ring-white ${badgeColor}`}
                          data-testid={`proof-thumb-badge-${pn}`}
                          data-resolved={allResolved ? "all" : "some"}
                        >
                          {allResolved ? <Check className="w-3 h-3" /> : total}
                        </span>
                      )}
                    </button>
                  );
                })}
                </div>
              </aside>
            )}
          </Document>
      </div>

      {/* Sidebar */}
      <div className="flex flex-col gap-3 min-h-0">
        {/* Resolution progress — total items still to resolve across the
            whole proof. Flips to a green "All Items Resolved" state once
            everything's ticked off, so the user knows at a glance whether
            they still need to scan through pages. */}
        {(() => {
          const totalComments = comments.length;
          const totalDrawings = annotations.length;
          const totalItems = totalComments + totalDrawings;
          if (totalItems === 0) return null;
          const unresolvedComments = comments.filter((c) => !c.resolved).length;
          const unresolvedDrawings = annotations.filter((a) => !a.resolved).length;
          const unresolved = unresolvedComments + unresolvedDrawings;
          const allDone = unresolved === 0;
          return (
            <div
              className={`rounded-xl p-4 border-2 transition-colors ${
                allDone
                  ? "bg-emerald-50 border-emerald-300"
                  : "bg-amber-50 border-amber-300"
              }`}
              data-testid="proof-resolution-summary"
              data-all-resolved={allDone ? "true" : "false"}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className={`grid place-items-center w-8 h-8 rounded-full shrink-0 text-white shadow-sm ${
                    allDone ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                >
                  {allDone ? <Check className="w-4 h-4" /> : <span className="text-sm font-bold tabular-nums">{unresolved}</span>}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`font-display text-sm font-bold leading-tight ${allDone ? "text-emerald-900" : "text-amber-900"}`}>
                    {allDone
                      ? "All Items Resolved"
                      : `${unresolved} item${unresolved === 1 ? "" : "s"} still to resolve`}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${allDone ? "text-emerald-800/80" : "text-amber-800/80"}`}>
                    {allDone
                      ? `Every note and drawing on this proof is marked resolved (${totalItems} total).`
                      : (() => {
                          const parts = [];
                          if (unresolvedComments > 0) parts.push(`${unresolvedComments} note${unresolvedComments === 1 ? "" : "s"}`);
                          if (unresolvedDrawings > 0) parts.push(`${unresolvedDrawings} drawing${unresolvedDrawings === 1 ? "" : "s"}`);
                          return `${parts.join(" · ")} across ${totalItems} total item${totalItems === 1 ? "" : "s"}.`;
                        })()}
                  </p>
                </div>
              </div>

              {/* "Jump to next item" — cycles through every unresolved item
                  (comments + drawings) in page order. Hidden once everything
                  is resolved. Tracks the currently-active item so repeated
                  clicks step through one-by-one rather than re-selecting the
                  same item. */}
              {!allDone && (
                <button
                  type="button"
                  onClick={() => {
                    // Build the ordered queue of unresolved items.
                    const queue = [
                      ...comments.filter((c) => !c.resolved).map((c) => ({ kind: "comment", item: c, page: c.page })),
                      ...annotations.filter((a) => !a.resolved).map((a) => ({ kind: "annotation", item: a, page: a.page })),
                    ].sort((a, b) => {
                      if (a.page !== b.page) return a.page - b.page;
                      // Stable secondary order by creation timestamp so the
                      // sequence is predictable across reloads.
                      const at = new Date(a.item.created_at || 0).getTime();
                      const bt = new Date(b.item.created_at || 0).getTime();
                      return at - bt;
                    });
                    if (queue.length === 0) return;
                    // Pick the next one after the currently-active item; fall
                    // back to "next item after the current page" if no item is
                    // active, and wrap to the start if we're at the end.
                    let nextIdx = 0;
                    if (activeComment) {
                      const curIdx = queue.findIndex((q) => q.kind === "comment" && q.item.id === activeComment.id);
                      nextIdx = curIdx >= 0 ? (curIdx + 1) % queue.length : 0;
                    } else {
                      const onOrAfter = queue.findIndex((q) => q.page > page);
                      nextIdx = onOrAfter >= 0 ? onOrAfter : 0;
                    }
                    const target = queue[nextIdx];
                    goToPage(target.page);
                    // Highlight the comment in the list so the user can spot
                    // it instantly — only applies to pinned notes; drawings
                    // simply land the user on the right page.
                    if (target.kind === "comment") {
                      setActiveComment(target.item);
                    } else {
                      setActiveComment(null);
                    }
                  }}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors shadow-sm"
                  data-testid="proof-jump-next-unresolved"
                  title="Jump to the next page that still has an unresolved item"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  Jump to next item
                </button>
              )}
            </div>
          );
        })()}

        {/* Reviewers summary */}
        {(() => {
          // Collect unique authors from comments, annotations, and page_notes
          const authorMap = new Map();
          comments.forEach((c) => {
            const name = (c.author_name || "").trim();
            if (!name) return;
            const key = `${name.toLowerCase()}|${c.author_role || "client"}`;
            const existing = authorMap.get(key) || { name, role: c.author_role || "client", count: 0 };
            existing.count += 1;
            authorMap.set(key, existing);
          });
          annotations.forEach((a) => {
            const name = (a.author_name || "").trim();
            if (!name) return;
            const key = `${name.toLowerCase()}|${a.author_role || "client"}`;
            const existing = authorMap.get(key) || { name, role: a.author_role || "client", count: 0 };
            existing.count += 1;
            authorMap.set(key, existing);
          });
          const reviewers = Array.from(authorMap.values()).sort((a, b) => b.count - a.count);
          if (reviewers.length === 0) return null;
          return (
            <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="proof-reviewers">
              <div className="flex items-center gap-1.5 mb-2">
                <Users className="w-3.5 h-3.5 text-zinc-500" />
                <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">Reviewers</p>
                <span className="ml-auto text-[10px] text-zinc-500">{reviewers.length}</span>
              </div>
              <ul className="space-y-1">
                {reviewers.map((r) => (
                  <li key={`${r.name}-${r.role}`} className="flex items-center justify-between gap-2 text-xs" data-testid={`proof-reviewer-${r.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${r.role === "designer" ? "bg-blue-500" : "bg-amber-500"}`} />
                      <span className="text-zinc-900 font-medium truncate">{r.name}</span>
                      {r.role === "designer" && <span className="text-[10px] text-zinc-500">(studio)</span>}
                    </span>
                    <span className="text-[10px] font-medium text-zinc-500 shrink-0">{r.count} note{r.count !== 1 ? "s" : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {pendingPin && (
          <NewNoteModal
            open={!!pendingPin}
            page={page}
            replyRole={replyRole}
            onUploadAttachment={onUploadAttachment}
            onSubmit={submitPendingPin}
            onCancel={cancelPendingPin}
          />
        )}

        {pendingDrawing && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 animate-fade-in" data-testid="proof-pending-drawing">
            <p className="text-sm font-bold text-red-900 mb-2">Describe what you&apos;re highlighting</p>
            <p className="text-xs text-red-800/80 mb-2">Tell the designer what&apos;s wrong with the area you&apos;ve drawn around.</p>
            <Textarea
              autoFocus
              rows={3}
              value={pendingDrawing.text}
              onChange={(e) => setPendingDrawing({ ...pendingDrawing, text: e.target.value })}
              placeholder="e.g. This colour needs to be greener…"
              data-testid="proof-pending-drawing-text"
              className="!text-sm"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={cancelPendingDrawing} className="px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 rounded-full" data-testid="proof-pending-drawing-cancel">Discard drawing</button>
              <Btn size="sm" onClick={submitPendingDrawing} disabled={!pendingDrawing.text.trim()} data-testid="proof-pending-drawing-submit">Save</Btn>
            </div>
          </div>
        )}

        {/* Removed the separate "active comment" detail box that used to open at the
            top — selecting a note now expands it in place in the list below so the
            user can copy/paste straight from there. */}


        {/* "General notes for page X" panel was removed (2026-06-08) — it
            encouraged clients to type change requests there without pinning
            them to a spot on the page, so those entries never showed up as
            resolvable items. New entries are no longer possible. For
            backward compatibility, any *existing* page_notes content is
            surfaced below as a read-only legacy panel so the designer can
            still see what was written on older proofs. */}
        {pageNotes && pageNotes[String(page)] && pageNotes[String(page)].trim() && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4" data-testid="proof-legacy-page-notes">
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Legacy general notes · page {page}</p>
            <p className="text-[11px] text-zinc-500 italic mb-2">This page-level note was written before the general-notes panel was removed. Read-only.</p>
            <p className="text-sm text-zinc-800 whitespace-pre-line leading-relaxed select-text">{pageNotes[String(page)]}</p>
          </div>
        )}

        <div className="bg-white border border-zinc-200 rounded-xl p-4 flex-1 overflow-y-auto" data-testid="proof-comments-list">
          <p className="text-base font-bold text-zinc-950 mb-3">
            Notes on page {page}
            <span className="ml-2 text-xs font-medium text-zinc-500">
              ({pageComments.length} note{pageComments.length !== 1 ? "s" : ""}, {pageAnnotations.length} drawing{pageAnnotations.length !== 1 ? "s" : ""})
            </span>
          </p>
          {pageComments.length === 0 && pageAnnotations.length === 0 ? (
            <p className="text-sm text-zinc-500 italic py-3">Nothing on this page yet.</p>
          ) : (
            <ul className="space-y-2">
              {pageComments.map((c) => {
                const idx = comments.findIndex((x) => x.id === c.id);
                const isActive = activeComment?.id === c.id;
                return (
                  <li
                    key={c.id}
                    onClick={() => setActiveComment(c)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${isActive ? "border-zinc-950 bg-zinc-50 ring-1 ring-zinc-300" : "border-zinc-200 hover:bg-zinc-50"}`}
                    data-testid={`proof-comment-item-${c.id}`}
                    data-active={isActive ? "true" : "false"}
                  >
                    <div className="flex items-start gap-2.5">
                      <span className={`mt-0.5 flex-shrink-0 grid place-items-center w-6 h-6 rounded-full text-[11px] font-bold text-white ${c.resolved ? "bg-emerald-500" : c.author_role === "designer" ? "bg-blue-600" : "bg-amber-500"}`}>{idx + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <p className="text-xs font-bold text-zinc-900">
                            {c.author_name || (c.author_role === "designer" ? "Designer" : "Client")}
                            {c.author_role === "designer" && <span className="ml-1.5 text-[9px] text-blue-700 font-semibold uppercase tracking-wider bg-blue-50 px-1.5 py-0.5 rounded">Designer</span>}
                            {c.resolved && <span className="ml-1.5 text-[9px] text-emerald-700 font-semibold uppercase tracking-wider bg-emerald-50 px-1.5 py-0.5 rounded">Resolved</span>}
                          </p>
                          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            {onResolveComment && (
                              <button
                                type="button"
                                onClick={() => onResolveComment(c.id, !c.resolved)}
                                className={`text-[10px] font-medium px-2 py-1 rounded-full inline-flex items-center gap-1 ${c.resolved ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
                                data-testid={`proof-comment-resolve-${c.id}`}
                              ><Check className="w-3 h-3" />{c.resolved ? "Resolved" : "Mark resolved"}</button>
                            )}
                            {canDelete(c) && onDeleteComment && (
                              <button
                                type="button"
                                onClick={() => onDeleteComment(c.id)}
                                className="text-[10px] text-red-600 hover:underline"
                                data-testid={`proof-comment-list-delete-${c.id}`}
                              >Remove</button>
                            )}
                          </div>
                        </div>
                        {/* Full text shown when this note is selected so the user can
                            copy/paste straight from the list; collapsed to 3 lines
                            otherwise to keep the list scannable. select-text guarantees
                            the user can highlight & copy without the row's onClick
                            interfering. */}
                        <p
                          className={`text-sm text-zinc-800 whitespace-pre-line mt-1 leading-relaxed select-text ${isActive ? "" : "line-clamp-3"}`}
                          onClick={(e) => { if (isActive) e.stopPropagation(); }}
                          onMouseDown={(e) => { if (isActive) e.stopPropagation(); }}
                          data-testid={`proof-comment-text-${c.id}`}
                        >{c.text}</p>
                        {/* Threaded reply panel — only mounted when this note is
                            selected, to keep the list scannable. The composer
                            inside it sends the reply (and, on the designer
                            side, fires an email to the chosen contact). */}
                        {isActive && (
                          <NoteReplies
                            comment={c}
                            onAddReply={onAddReply}
                            replyRole={replyRole}
                            replyContacts={replyContacts}
                          />
                        )}
                        {/* Image attachments — only available on the public/
                            client side via onUploadAttachment. Designer can
                            still SEE the thumbnails (canEdit=false) but not
                            upload/delete to keep the client→designer flow
                            uncluttered. */}
                        {isActive && (c.attachments?.length > 0 || onUploadAttachment) && attachmentBaseUrl && (
                          <NoteAttachments
                            comment={c}
                            attachmentBaseUrl={attachmentBaseUrl}
                            onUpload={onUploadAttachment}
                            onDelete={onDeleteAttachment}
                            canEdit={!!onUploadAttachment && replyRole === "client"}
                          />
                        )}
                        {/* Collapsed badge: when the note isn't active but has replies,
                            show a count so the user knows there's a conversation. */}
                        {!isActive && (c.replies?.length > 0) && (
                          <p className="mt-1 text-[11px] text-zinc-500 inline-flex items-center gap-1" data-testid={`proof-comment-reply-count-${c.id}`}>
                            <CornerDownRight className="w-3 h-3" />
                            {c.replies.length} repl{c.replies.length === 1 ? "y" : "ies"} — open to view
                          </p>
                        )}
                        {!isActive && (c.attachments?.length > 0) && (
                          <p className="mt-1 text-[11px] text-zinc-500 inline-flex items-center gap-1" data-testid={`proof-comment-attachment-count-${c.id}`}>
                            <ImageIcon className="w-3 h-3" />
                            {c.attachments.length} image{c.attachments.length === 1 ? "" : "s"} attached
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
              {pageAnnotations.map((a) => (
                <li key={a.id} className={`p-3 border rounded-lg ${a.resolved ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/40"}`} data-testid={`proof-annotation-item-${a.id}`}>
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 grid place-items-center w-6 h-6 rounded-full text-white shrink-0 ${a.resolved ? "bg-emerald-500" : "bg-red-500"}`}>
                      {a.resolved ? <Check className="w-3 h-3" /> : <Pencil className="w-3 h-3" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className="text-xs font-bold text-zinc-900">
                          {a.author_name || (a.author_role === "designer" ? "Designer" : "Client")}
                          <span className={`ml-1.5 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${a.resolved ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {a.resolved ? "Resolved" : "Drawing"}
                          </span>
                          {a.author_role === "designer" && <span className="ml-1 text-[9px] text-blue-700 font-semibold uppercase tracking-wider bg-blue-50 px-1.5 py-0.5 rounded">Designer</span>}
                        </p>
                        <div className="flex items-center gap-1 shrink-0">
                          {onResolveAnnotation && (
                            <button
                              type="button"
                              onClick={() => onResolveAnnotation(a.id, !a.resolved)}
                              className={`text-[10px] font-medium px-2 py-1 rounded-full inline-flex items-center gap-1 ${a.resolved ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
                              data-testid={`proof-annotation-resolve-${a.id}`}
                            ><Check className="w-3 h-3" />{a.resolved ? "Resolved" : "Mark resolved"}</button>
                          )}
                          {canDeleteAnnotation(a) && onDeleteAnnotation && (
                            <button
                              type="button"
                              onClick={() => onDeleteAnnotation(a.id)}
                              className="text-[10px] text-red-600 hover:underline"
                              data-testid={`proof-annotation-list-delete-${a.id}`}
                            >Remove</button>
                          )}
                        </div>
                      </div>
                      {a.text && <p className="text-sm text-zinc-800 mt-1 leading-relaxed whitespace-pre-line">{a.text}</p>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfProofViewer;
